import React, { useState, useContext } from 'react';
import api from '../../api';
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode'; // Import aggiornato in base alla nuova convenzione
import { AuthContext } from '../../context/authContext';
import InputField from '../../components/InputField';
import { showToast } from '../../components/toast/ToastNotifier';
import { useTranslation } from 'react-i18next';
import RegisterButton from '../../components/button/registerButton';
import SEO from '../../components/SEO'


const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState(''); // Stato per eventuale 2FA
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();

  const navigate = useNavigate();
  const { setIsAuthenticated } = useContext(AuthContext);

  const isFormValid = () => {
    return email.trim() !== '' && password.trim() !== '';
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    if (!isFormValid()) {
      showToast.error('Compila tutti i campi prima di effettuare il login!');
      return;
    }

    try {
      const response = await api.post('/auth/login', {
        email,
        password,
      });

      // Salva i token restituiti dal backend
      localStorage.setItem('authToken', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.accessToken}`;
      setIsAuthenticated(true);

      const { twoFactorEnabled } = response.data.user;
      if (twoFactorEnabled) {
        setShowTwoFactor(true);
      } else {
        // Decodifica il token per ottenere il ruolo
        const decodedToken = jwtDecode(response.data.accessToken);
        const userRole = decodedToken.role
        handlePostLogin(userRole);
      }
    } catch (error) {
      showToast.error(
        error.response ? error.response.data.message : 'Login failed due to unknown error'
      );
    }
  };

  const handlePostLogin = (userRole) => {
    redirectToRolePage(userRole);
  };

  const handleTwoFactorVerify = async () => {
    try {
      const response = await api.post('/twoAuth/2fa/verify', { token });
      if (response.status === 200) {
        const authToken = localStorage.getItem('authToken');
        const decodedToken = jwtDecode(authToken);
        const userRole = decodedToken.role;
        handlePostLogin(userRole);
      } else {
        showToast.error('Token 2FA non valido');
      }
    } catch (error) {
      showToast.error('Errore nella verifica del token 2FA');
    }
  };

  const redirectToRolePage = (userRole) => {
    const roleToPage = {
      customer: '/',
      admin: '/admin-dashboard',
      care: '/customer-care',
      investor: '/investor-dashboard',
      amministrator: '/amministrator',
    };

    // Se c'è una pagina di provenienza, usala. Altrimenti, usa quella in base al ruolo.
    const redirectTo = location.state?.from || roleToPage[userRole] || '/';
    navigate(redirectTo);
  };

  const handlePasswordResetRequest = async () => {
    try {
      await api.post('/auth/request-password-reset', { email });
      showToast.success('Se l\'email è registrata, ti abbiamo inviato un link per resettare la tua password.');
    } catch (error) {
      showToast.info('Inserisci la mail per fare il reset della password.');
    }
  };

  const formIsValid = isFormValid();

  return (
    <>
    <SEO
      title="Login - Save Your Travel"
      description="Accedi alla piattaforma Save Your Travel per gestire i tuoi viaggi, monitorare le prenotazioni e i tuoi annunci e partecipare ad aste esclusive in tutta sicurezza."
      keywords={[
        "Piattaforma viaggi",
        "Prenotazioni",
        "Annunci di viaggio",
        "login",
        "Aste esclusive",
        "Viaggi annullati",
        "Rivendita viaggi"
      ]}
      frequency="week"
      priority="0.6"
    />

      <div className="container my-32">
        <div className='uppercase font-heading text-3xl font-semibold mb-8 border-b-2 pb-8'>
          {t("login.title")}
        </div>

        <div className="items-center lg:w-2/5 mx-auto bg-gray-100">
          <div className="p-12 bg-white shadow-md rounded-lg">
            <div className='pb-8 text-start'>
              <h2 className='font-heading text-2xl mb-2 uppercase text-wild-ocean font-semibold'>Accedi al tuo account</h2>
              <label className='text-sm'>{t("login.noAccount")}</label>
              <RegisterButton />
            </div>

            {!showTwoFactor ? (
              <form onSubmit={handleLogin}>
                <InputField
                  type="email"
                  name="email"
                  label="Email"
                  value={email}
                  handleChange={(e) => setEmail(e.target.value)}
                  required
                />

                <InputField
                  type="password"
                  name="password"
                  label="Password"
                  value={password}
                  handleChange={(e) => setPassword(e.target.value)}
                  required
                />

                <button
                  type="submit"
                  className={`lg:w-1/2 w-full mx-auto flex justify-center text-white uppercase font-heading font-semibold px-4 py-2 rounded-lg shadow-md transition-colors duration-300 ${formIsValid ? 'bg-wild-ocean hover:bg-green-adventure' : 'bg-gray-300 cursor-not-allowed'
                    }`}
                  disabled={!formIsValid}
                >
                  {t("login.login")}
                </button>

                <div className="flex justify-between mt-4">
                  <button
                    type="button"
                    className="text-sm font-heading uppercase font-semibold text-wild-ocean hover:underline"
                    onClick={handlePasswordResetRequest}
                  >
                    {t("login.reset")}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                <InputField
                  name="token"
                  label="2FA Token"
                  value={token}
                  handleChange={(e) => setToken(e.target.value)}
                />
                <button
                  onClick={handleTwoFactorVerify}
                  className="w-full bg-sky-700 text-white uppercase font-heading font-semibold px-4 py-2 rounded-lg shadow-md hover:bg-green-adventure"
                >
                  {t("login.verifica")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>

  );
};

export default Login;